import React, { Suspense, lazy, useMemo } from 'react';

const EmailEditor = lazy(() => import('react-email-editor'));

/** Export HTML + design JSON from a react-email-editor ref (Unlayer). */
export async function exportFromEmailEditorRef(ref) {
  const ed = ref?.current?.editor;
  if (!ed) {
    return { html: '', design: null };
  }
  const html = await new Promise((resolve) => {
    ed.exportHtml((data) => resolve(data?.html ?? ''));
  });
  let design = null;
  if (typeof ed.saveDesign === 'function') {
    try {
      design = await new Promise((resolve) => {
        ed.saveDesign((d) => resolve(d ?? null));
      });
    } catch {
      design = null;
    }
  }
  return { html: html || '', design };
}

const baseOptions = (projectIdEnv) => {
  const pid = projectIdEnv && String(projectIdEnv).trim();
  return {
    displayMode: 'email',
    ...(pid ? { projectId: Number(pid) } : {}),
    appearance: {
      theme: 'light',
    },
    features: {
      preview: true,
      undoRedo: true,
    },
  };
};

/**
 * Two Unlayer instances: header strip + footer strip (separate exports).
 */
export default function AnnouncementUnlayerEditors({ headerRef, footerRef, headerDesign, footerDesign }) {
  const options = useMemo(
    () => baseOptions(process.env.REACT_APP_UNLAYER_PROJECT_ID),
    [],
  );

  return (
    <div className="tann-unlayer-stack">
      <Suspense
        fallback={
          <div className="tann-unlayer-loading" role="status">
            Loading drag-and-drop email designer…
          </div>
        }
      >
        <section className="tann-unlayer-block" aria-label="Email header, drag blocks from the right">
          <div className="tann-unlayer-block-head">
            <h3 className="tann-unlayer-block-title">Header</h3>
            <p className="tann-unlayer-block-desc">
              Logo, brand bar, navigation — appears above the announcement card.
            </p>
          </div>
          <div className="tann-unlayer-frame">
            <EmailEditor
              ref={headerRef}
              editorId="ecdt-announcement-header"
              minHeight={360}
              onReady={(unlayer) => {
                if (headerDesign) {
                  try {
                    unlayer.loadDesign(headerDesign);
                  } catch {
                    /* ignore */
                  }
                }
              }}
              options={options}
            />
          </div>
        </section>

        <section className="tann-unlayer-block" aria-label="Email footer, drag blocks from the right">
          <div className="tann-unlayer-block-head">
            <h3 className="tann-unlayer-block-title">Footer</h3>
            <p className="tann-unlayer-block-desc">Address, legal, or unsubscribe note — below the card.</p>
          </div>
          <div className="tann-unlayer-frame">
            <EmailEditor
              ref={footerRef}
              editorId="ecdt-announcement-footer"
              minHeight={300}
              onReady={(unlayer) => {
                if (footerDesign) {
                  try {
                    unlayer.loadDesign(footerDesign);
                  } catch {
                    /* ignore */
                  }
                }
              }}
              options={options}
            />
          </div>
        </section>
      </Suspense>
    </div>
  );
}
